import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, Sparkles, ShieldAlert } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import { AiSurface } from '@/components/ui/ai-surface';
import { cn } from '@/lib/utils/cn';
import type { ClassificationItem } from '@/lib/api/documents';
import type { RoutingProposal } from '@/lib/api/review-proposals';

/**
 * T-0099 / E10 — AI queue banner sitting above the documents library.
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/screens-engagement.js`
 * → `aiQueueBanner()` helper at L344 (the alert banner at the top of the
 * documents library that links into the review queue).
 *
 * Two pieces of state feed this banner:
 *   1. Documents still moving through the parse/index/classify pipeline —
 *      surfaced via `pendingItems`. Each pending item shows a spinner and
 *      a short status label. The list collapses by default so the banner
 *      stays compact; the user clicks the chevron to see per-doc progress.
 *   2. Open AI proposals waiting on consultant review — split into
 *      baseline-forming vs ongoing. The baseline split is the
 *      no-auto-accept zone (E02-F06): baseline-forming items always need
 *      consultant review regardless of confidence, so the banner calls
 *      them out separately with a shield icon.
 *
 * The "Open review queue" CTA links to `/engagements/:id/documents/review`
 * — that route owns the side-by-side review surface (also implemented by
 * T-0099).
 */

export interface AiQueueBannerProps {
  engagementId: string;
  /** Documents still in the ingestion pipeline (status not classified yet). */
  pendingItems: ClassificationItem[];
  /** AI proposals awaiting consultant review (decision === 'pending'). */
  proposals: RoutingProposal[];
  className?: string;
}

export function AiQueueBanner({
  engagementId,
  pendingItems,
  proposals,
  className,
}: AiQueueBannerProps) {
  const [expanded, setExpanded] = useState(false);

  const baselineCount = proposals.filter((p) => p.is_baseline).length;
  const ongoingCount = proposals.length - baselineCount;

  // If nothing is pending and no proposals are open, render nothing —
  // the banner is only meaningful when there's work for the consultant.
  if (pendingItems.length === 0 && proposals.length === 0) return null;

  return (
    <AiSurface
      as="div"
      className={cn('rounded-card border border-ai-surface-border p-4', className)}
    >
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex h-9 w-9 flex-none items-center justify-center rounded-card bg-ai-surface-border text-ai-ink">
          <Sparkles className="h-4 w-4" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-body-sm font-semibold text-ai-ink">
            <span>
              {proposals.length} proposal{proposals.length === 1 ? '' : 's'}
              {proposals.length > 0 ? (
                <>
                  {' · '}
                  <span className="text-ink-900">{baselineCount} baseline-forming</span>
                  {' + '}
                  <span className="text-ink-900">{ongoingCount} ongoing</span>
                </>
              ) : null}
            </span>
            {pendingItems.length > 0 ? (
              <span className="inline-flex items-center gap-1.5 text-ink-700">
                <Spinner className="h-3 w-3" />
                <span>
                  {pendingItems.length} doc{pendingItems.length === 1 ? '' : 's'} parsing
                </span>
              </span>
            ) : null}
          </div>
          <div className="mt-1 text-meta text-ink-700">
            Baseline items require consultant review even at high confidence. Tag + one-sentence
            rationale required on any value change.
          </div>
          {pendingItems.length > 0 ? (
            <button
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              aria-expanded={expanded}
              className="mt-2 inline-flex items-center gap-1 text-meta font-medium text-ai-ink hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
            >
              {expanded ? (
                <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              <span>{expanded ? 'Hide per-doc progress' : 'Show per-doc progress'}</span>
            </button>
          ) : null}
        </div>
        {proposals.length > 0 ? (
          <Button asChild size="sm">
            <Link to="/engagements/$engagementId/proposals" params={{ engagementId }}>
              <ShieldAlert className="h-4 w-4" aria-hidden="true" />
              <span>Open review queue</span>
            </Link>
          </Button>
        ) : null}
      </div>
      {expanded && pendingItems.length > 0 ? (
        <ul
          aria-label="Document ingestion progress"
          className="mt-3 flex flex-col gap-1.5 rounded-card border border-ai-surface-border bg-paper-0 p-2"
        >
          {pendingItems.map((item) => (
            <li
              key={item.document_id}
              className="flex items-center gap-2 px-2 py-1 text-meta text-ink-700"
            >
              <Spinner className="h-3 w-3 flex-none" />
              <span className="flex-1 truncate text-ink-900">{item.document_title}</span>
              <span className="font-mono text-ink-500">{item.status || 'pending'}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </AiSurface>
  );
}
